import {WatchSource} from '@src/Navigation/types/watchPageRouteParams.type';
import {
  IAccessTypParam,
  IPaywallParam,
} from '@src/Umbraco/Components/PaywallChecker/PaywallChecker';
import {IGetMainContentListByIdQuery} from '@src/Api/Graphql/gql/types.generated';

export interface ExtractedVideoParams {
  id: string;
  byteplusId?: string;
  source: WatchSource;
  sourceUrl?: string;
  videoUrl?: string;
  vimeoId?: string;
  youtubeId?: string;
  paywallData?: {
    accessType?: IAccessTypParam;
    paywall?: IPaywallParam;
  };
  title: string;
  isLive?: boolean;
  __typename?: string;
}

/**
 * Extrait les paramètres vidéo depuis les données Umbraco
 * Supporte à la fois les données GraphQL et REST API
 */
export function extractVideoParamsFromUmbraco(
  umbracoData: IGetMainContentListByIdQuery | any,
  contentId: string,
): ExtractedVideoParams | null {
  let contentItem: any = null;
  let contentType: string = '';

  // Détecter le format des données
  if (umbracoData?.allContent?.items) {
    // Format GraphQL
    const items = umbracoData.allContent.items;
    contentItem = items.find((item: any) => item?.id === contentId);
    contentType = contentItem?.__typename || '';
  } else if (umbracoData?.contentTypeAlias) {
    // Format REST API
    contentItem = umbracoData;
    contentType = umbracoData.contentTypeAlias;
  }

  if (!contentItem) {
    console.warn(`Content item with id ${contentId} not found in umbracoData`);
    return null;
  }

  if (!contentType) {
    console.warn(`Content type not found for item ${contentId}`);
    return null;
  }

  console.log('contentItem', contentItem);
  const title = contentItem.title || contentItem.name || '';

  // Extraction selon le type de contenu
  switch (contentType) {
    case 'VODItem': {
      const vodItem = contentItem as any; // Type assertion pour accéder aux champs

      // Déterminer la source et les IDs
      let source = WatchSource.VIMEO;
      let byteplusId: string | undefined;
      let vimeoId: string | undefined;
      let youtubeId: string | undefined;
      let sourceUrl: string | undefined;
      let videoUrl: string | undefined;

      // Priorité : Byteplus > YouTube > Vimeo
      if (vodItem.byteplusVideoID) {
        source = WatchSource.BYTEPLUS;
        byteplusId = vodItem.byteplusVideoID;
      } else if (vodItem.youtubeId) {
        source = WatchSource.LOCAL; // YouTube géré différemment
        youtubeId = vodItem.youtubeId;
      } else if (vodItem.liveEventId) {
        // Pour Vimeo, liveEventId contient l'ID Vimeo
        vimeoId = vodItem.liveEventId.toString();
        sourceUrl = vodItem.sourceUrl;
        videoUrl = vodItem.vimeoHlsUrl || vodItem.sourceUrl;
      }

      return {
        id: contentId,
        byteplusId,
        source,
        sourceUrl,
        videoUrl,
        vimeoId,
        youtubeId,
        paywallData: {
          accessType: vodItem.accessType,
          paywall: vodItem.paywall,
        },
        title: title || '',
        isLive: false,
      };
    }

    case 'LiveEvent': {
      const liveEvent = contentItem as any;

      return {
        id: contentId,
        source: WatchSource.LIVESTREAM,
        sourceUrl: liveEvent.sourceUrl || liveEvent.url || '',
        paywallData: {
          accessType: liveEvent.accessType,
          paywall: liveEvent.paywall,
        },
        title: title || '',
        isLive: true,
      };
    }

    case 'Channel':
    case 'channel': {
      const channel = contentItem as any;

      return {
        id: contentId,
        __typename: 'Channel',
        source: WatchSource.LIVESTREAM,
        sourceUrl: channel.sourceUrl || channel.url || '',
        paywallData: {
          accessType: channel.accessType,
          paywall: channel.paywall,
        },
        title: title || '',
        isLive: true,
      };
    }

    case 'Course':
    case 'course': {
      const course = contentItem as any;
      console.log('extractVideoParamsFromUmbraco - Course case:', {
        byteplusVideoID: course.byteplusVideoID,
        trailer: course.trailer,
        typeofTrailer: typeof course.trailer,
      });

      // Priorité : byteplusVideoID > trailer
      if (course.byteplusVideoID) {
        console.log('extractVideoParamsFromUmbraco - Using byteplusVideoID');
        return {
          source: WatchSource.BYTEPLUS,
          byteplusId: course.byteplusVideoID,
          paywallData: {
            accessType: course.accessType,
            paywall: course.paywall,
          },
          title: title || '',
          isLive: false,
        };
      } else if (course.trailer) {
        console.log('extractVideoParamsFromUmbraco - Using trailer');
        if (typeof course.trailer === 'string') {
          // Si c'est une string, c'est probablement une URL directe
          return {
            source: WatchSource.VIMEO, // Par défaut Vimeo pour les trailers
            sourceUrl: course.trailer,
            videoUrl: course.trailer,
            paywallData: {
              accessType: course.accessType,
              paywall: course.paywall,
            },
            title: title || '',
            isLive: false,
          };
        } else if (typeof course.trailer === 'number') {
          // Si c'est un nombre, c'est un liveEventId Vimeo
          return {
            source: WatchSource.VIMEO,
            vimeoId: course.trailer.toString(),
            paywallData: {
              accessType: course.accessType,
              paywall: course.paywall,
            },
            title: title || '',
            isLive: false,
          };
        }
      }

      // Si pas de byteplusVideoID ni trailer
      console.warn(`Course ${contentId} has no byteplusVideoID or trailer`);
      return null;
    }

    default:
      console.warn(`Unsupported content type: ${contentType}`);
      return null;
  }
}
